import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Phone, Menu, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setIsMobileMenuOpen(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      {/* Skip to content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-azure-500 focus:text-white focus:rounded-lg focus:ring-2 focus:ring-offset-2 focus:ring-azure-hover"
      >
        Skip to content
      </a>

      <header
        className={`fixed top-0 z-50 w-full transition-all duration-300 ${
          isScrolled
            ? "bg-[#051C2C]/85 backdrop-blur-md shadow-md animate-fade-in"
            : "bg-transparent"
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex h-24 items-center justify-between">
            {/* Logo */}
            <button
              onClick={scrollToTop}
              className="flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-azure-hover focus:ring-offset-2 rounded-lg transition-transform hover:scale-105 animate-fade-in"
              aria-label="ShoreSight Marine - Return to top"
            >
              <img
                src="/images/shoresight-logo-light.png"
                alt="ShoreSight Marine Logo"
                className="h-auto w-[160px] md:w-[180px] lg:w-[200px] transition-opacity duration-300"
                loading="eager"
              />
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
              <button
                onClick={() => scrollToSection("services")}
                className="text-sm font-medium text-white hover:text-azure-500 transition-colors focus:outline-none focus:ring-2 focus:ring-azure-hover focus:ring-offset-2 rounded animate-fade-in"
                style={{ animationDelay: '100ms' }}
              >
                Services
              </button>
              <button
                onClick={() => scrollToSection("gallery")}
                className="text-sm font-medium text-white hover:text-azure-500 transition-colors focus:outline-none focus:ring-2 focus:ring-azure-hover focus:ring-offset-2 rounded animate-fade-in"
                style={{ animationDelay: '200ms' }}
              >
                Gallery
              </button>
              <button
                onClick={() => scrollToSection("reviews")}
                className="text-sm font-medium text-white hover:text-azure-500 transition-colors focus:outline-none focus:ring-2 focus:ring-azure-hover focus:ring-offset-2 rounded animate-fade-in"
                style={{ animationDelay: '300ms' }}
              >
                Reviews
              </button>
              <button
                onClick={() => scrollToSection("faq")}
                className="text-sm font-medium text-white hover:text-azure-500 transition-colors focus:outline-none focus:ring-2 focus:ring-azure-hover focus:ring-offset-2 rounded animate-fade-in"
                style={{ animationDelay: '400ms' }}
              >
                FAQ
              </button>
              <button
                onClick={() => scrollToSection("contact")}
                className="text-sm font-medium text-white hover:text-azure-500 transition-colors focus:outline-none focus:ring-2 focus:ring-azure-hover focus:ring-offset-2 rounded animate-fade-in"
                style={{ animationDelay: '500ms' }}
              >
                Contact
              </button>
            </nav>

            {/* Call Button (Desktop) */}
            <div className="hidden md:flex items-center gap-4 animate-fade-in" style={{ animationDelay: '600ms' }}>
              <Button
                asChild
                size="sm"
                className="bg-navy-900 hover:bg-navy-800 text-azure-500 hover:text-azure-hover font-semibold px-4 py-2 rounded-lg focus:ring-2 focus:ring-offset-2 focus:ring-azure-hover transition-colors"
              >
                <a href="tel:6193774673" aria-label="Call ShoreSight Marine at (619) 377-4673">
                  <Phone className="mr-2 h-4 w-4" />
                  (619) 377-4673
                </a>
              </Button>
            </div>

            {/* Mobile: Call Button + Menu */}
            <div className="flex md:hidden items-center gap-2 animate-fade-in" style={{ animationDelay: '100ms' }}>
              <Button
                asChild
                size="sm"
                className="bg-navy-900 hover:bg-navy-800 text-azure-500 hover:text-azure-hover font-semibold px-3 py-2 rounded-lg focus:ring-2 focus:ring-offset-2 focus:ring-azure-hover"
              >
                <a href="tel:6193774673" aria-label="Call ShoreSight Marine at (619) 377-4673">
                  <Phone className="h-4 w-4" />
                </a>
              </Button>

              <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-white hover:bg-white/10 focus:ring-2 focus:ring-azure-hover focus:ring-offset-2"
                    aria-label="Open navigation menu"
                  >
                    {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="bg-navy-900 border-l border-azure-500/20 w-[280px]">
                  <nav className="flex flex-col gap-6 mt-8" aria-label="Mobile navigation">
                    <button
                      onClick={() => scrollToSection("services")}
                      className="text-lg font-medium text-white hover:text-azure-500 transition-colors text-left focus:outline-none focus:ring-2 focus:ring-azure-hover rounded px-2 py-1"
                    >
                      Services
                    </button>
                    <button
                      onClick={() => scrollToSection("gallery")}
                      className="text-lg font-medium text-white hover:text-azure-500 transition-colors text-left focus:outline-none focus:ring-2 focus:ring-azure-hover rounded px-2 py-1"
                    >
                      Gallery
                    </button>
                    <button
                      onClick={() => scrollToSection("reviews")}
                      className="text-lg font-medium text-white hover:text-azure-500 transition-colors text-left focus:outline-none focus:ring-2 focus:ring-azure-hover rounded px-2 py-1"
                    >
                      Reviews
                    </button>
                    <button
                      onClick={() => scrollToSection("faq")}
                      className="text-lg font-medium text-white hover:text-azure-500 transition-colors text-left focus:outline-none focus:ring-2 focus:ring-azure-hover rounded px-2 py-1"
                    >
                      FAQ
                    </button>
                    <button
                      onClick={() => scrollToSection("contact")}
                      className="text-lg font-medium text-white hover:text-azure-500 transition-colors text-left focus:outline-none focus:ring-2 focus:ring-azure-hover rounded px-2 py-1"
                    >
                      Contact
                    </button>

                    <Button
                      asChild
                      size="lg"
                      className="bg-navy-900 hover:bg-navy-800 text-azure-500 hover:text-azure-hover font-semibold mt-4 w-full focus:ring-2 focus:ring-offset-2 focus:ring-azure-hover"
                    >
                      <a href="tel:6193774673" aria-label="Call ShoreSight Marine at (619) 377-4673">
                        <Phone className="mr-2 h-5 w-5" />
                        (619) 377-4673
                      </a>
                    </Button>
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};
